<style>
    .is-invalid-button {
        border: 1px solid#D33638;
    }
</style>

<script>
    import { onMount } from 'svelte';
    import { createEventDispatcher } from 'svelte';
    import { SlideEditor } from '../methods/SlideEditor.js';
    import { isPlainObject } from '../methods/isPlainObject.js';
    import { isEmpty } from '../methods/isEmpty.js';
    import { submitButtonControl } from '../methods/submitButtonControl.js';

    export let mtappVars;
    export let id;
    export let storage;
    export let storageKey;
    export let handle;
    export let fieldBlock;

    if (fieldBlock.type === 'splitField' && !isPlainObject(storage[storageKey])) {
        storage[storageKey] = {};
        fieldBlock.options.forEach((option) => {
            storage[storageKey][option.key] = '';
        });
    }

    let validationEnabled = false;
    let hasError = false;

    function validateField(event, _id = id, value = storage[storageKey]) {
        validationEnabled = true;
        if (fieldBlock.required) {
            // 必須項目のエラーチェック
            hasError = isEmpty(value) && validationEnabled;
        } else {
            // 必須でない場合はエラーをクリア
            hasError = false;
        }
        if (hasError) {
            mtappVars.__system.multiFieldErrors[_id] = 1;
            mtappVars.__system.submitButtonControlHasError = true;
            submitButtonControl('hasError');
        } else {
            delete mtappVars.__system.multiFieldErrors[_id];
        }
        if (isEmpty(mtappVars.__system.multiFieldErrors)) {
            submitButtonControl('submittable');
        }
    }

    const dispatch = createEventDispatcher();
    function triggerInputChange() {
        dispatch('inputchange');
    }

    onMount(() => {
        if (fieldBlock.type === 'splitField') {
            fieldBlock.options.forEach((option) => {
                validateField(null, id + '-' + option.key);
            });
        } else {
            validateField();
        }

        if (fieldBlock.type === 'tinymce') {
            const options = fieldBlock.options || {};
            const preset = fieldBlock.preset || '';
            new SlideEditor(id, options, preset);
        }
    });
</script>

<div data-handle={handle}>
    {#if ['text', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(fieldBlock.type)}
        <input
            type="text"
            class="form-control"
            class:is-invalid={hasError}
            id={id}
            bind:value={storage[storageKey]}
            on:blur={validateField}
            on:input={triggerInputChange} />
    {:else if fieldBlock.type === 'email'}
        <input
            type="email"
            class="form-control"
            class:is-invalid={hasError}
            id={id}
            bind:value={storage[storageKey]}
            on:blur={validateField}
            on:input={triggerInputChange} />
    {:else if fieldBlock.type === 'url'}
        <input
            type="url"
            class="form-control"
            class:is-invalid={hasError}
            id={id}
            bind:value={storage[storageKey]}
            on:blur={validateField}
            on:input={triggerInputChange} />
    {:else if fieldBlock.type === 'tel'}
        <input
            type="tel"
            class="form-control"
            class:is-invalid={hasError}
            id={id}
            bind:value={storage[storageKey]}
            on:blur={validateField}
            on:input={triggerInputChange} />
    {:else if fieldBlock.type === 'password'}
        <input
            type="password"
            class="form-control"
            class:is-invalid={hasError}
            id={id}
            bind:value={storage[storageKey]}
            on:blur={validateField}
            on:input={triggerInputChange} />
    {:else if fieldBlock.type === 'number'}
        <input
            type="number"
            class="form-control"
            class:is-invalid={hasError}
            id={id}
            bind:value={storage[storageKey]}
            on:blur={validateField}
            on:input={triggerInputChange} />
    {:else if fieldBlock.type === 'date'}
        <input
            type="date"
            class="form-control"
            class:is-invalid={hasError}
            id={id}
            bind:value={storage[storageKey]}
            on:change={validateField}
            on:input={triggerInputChange} />
    {:else if fieldBlock.type === 'time'}
        <input
            type="time"
            class="form-control"
            class:is-invalid={hasError}
            id={id}
            bind:value={storage[storageKey]}
            on:change={validateField}
            on:input={triggerInputChange} />
    {:else if fieldBlock.type === 'datepicker'}
        <input
            type="date"
            class="form-control"
            class:is-invalid={hasError}
            id={id}
            bind:value={storage[storageKey]}
            on:change={validateField}
            on:input={triggerInputChange} />
    {:else if fieldBlock.type === 'splitField'}
        <div class="d-md-flex gap-3">
            {#each fieldBlock.options as option}
                <div class="flex-fill">
                    <input
                        type="text"
                        class="form-control"
                        placeholder={option.placeholder || ''}
                        class:is-invalid={fieldBlock.required && isEmpty(storage[storageKey][option.key]) && validationEnabled}
                        bind:value={storage[storageKey][option.key]}
                        on:blur={(event) => {
                            validateField(event, id + '-' + option.key, storage[storageKey][option.key]);
                        }}
                        on:input={triggerInputChange} />
                    {#if fieldBlock.required && isEmpty(storage[storageKey][option.key]) && validationEnabled}
                        <span class="text-danger fw-bold">{mtappVars.translate['This field is required']}</span>
                    {/if}
                </div>
            {/each}
        </div>
    {:else if fieldBlock.type === 'textarea'}
        <textarea
            class="form-control"
            class:is-invalid={hasError}
            id={id}
            bind:value={storage[storageKey]}
            on:blur={validateField}
            on:input={triggerInputChange}></textarea>
    {:else if fieldBlock.type === 'code'}
        <textarea
            class="form-control font-monospace"
            class:is-invalid={hasError}
            id={id}
            bind:value={storage[storageKey]}
            on:blur={validateField}
            on:input={triggerInputChange}></textarea>
    {:else if fieldBlock.type === 'tinymce'}
        <textarea
            class="form-control"
            id={id}
            bind:value={storage[storageKey]}
            on:input={() => {
                validateField();
                triggerInputChange();
            }}></textarea>
        <div id={id + '-view'} class="mtapp-slide-editor-viewer">{@html storage[storageKey]}</div>
        <button id={id + '-button'} type="button" class="btn btn-default mt-3" class:is-invalid-button={hasError}
            >{mtappVars.translate['Edit']}</button>
    {:else if fieldBlock.type === 'select'}
        <select
            bind:value={storage[storageKey]}
            class="form-select"
            class:is-invalid={hasError}
            id={id}
            on:change={() => {
                validateField();
                triggerInputChange();
            }}>
            {#if !fieldBlock.removeEmptyOption}
                <option value="">{mtappVars.translate['Please select...']}</option>
            {/if}
            {#each fieldBlock.options as option}
                <option value={option.data}>{option.label}</option>
            {/each}
        </select>
    {:else if fieldBlock.type === 'select-multiple'}
        <select
            bind:value={storage[storageKey]}
            class="form-select"
            class:is-invalid={hasError}
            id={id}
            multiple
            on:change={() => {
                validateField();
                triggerInputChange();
            }}>
            {#each fieldBlock.options as option}
                <option value={option.data}>{option.label}</option>
            {/each}
        </select>
    {:else if fieldBlock.type === 'checkbox' && fieldBlock.options.length > 1}
        {#each fieldBlock.options as option, optionIdx}
            <div class="form-check form-check-inline mtapp-mf-field-checkbox">
                <input
                    type="checkbox"
                    class="form-check-input"
                    class:is-invalid={hasError}
                    value={option.data}
                    id={[id, optionIdx].join('-')}
                    bind:group={storage[storageKey]}
                    on:change={() => {
                        validateField();
                        triggerInputChange();
                    }} />
                <label class="form-check-label" for={[id, optionIdx].join('-')}>{option.label}</label>
            </div>
        {/each}
    {:else if fieldBlock.type === 'checkbox'}
        {#each fieldBlock.options as option, optionIdx}
            <div class="form-check form-check-inline mtapp-mf-field-checkbox">
                <input
                    type="checkbox"
                    class="form-check-input"
                    class:is-invalid={hasError}
                    value={option.data}
                    id={[id, optionIdx].join('-')}
                    bind:checked={storage[storageKey]}
                    on:change={() => {
                        validateField();
                        triggerInputChange();
                    }} />
                <label class="form-check-label" for={[id, optionIdx].join('-')}>{option.label}</label>
            </div>
        {/each}
    {:else if fieldBlock.type === 'radio'}
        {#each fieldBlock.options as option, optionIdx}
            <div class="form-check form-check-inline mtapp-mf-field-radio">
                <input
                    type="radio"
                    class="form-check-input"
                    class:is-invalid={hasError}
                    value={option.data}
                    id={[id, optionIdx].join('-')}
                    name={id}
                    bind:group={storage[storageKey]}
                    on:change={() => {
                        validateField();
                        triggerInputChange();
                    }} />
                <label class="form-check-label" for={[id, optionIdx].join('-')}>{option.label}</label>
            </div>
        {/each}
    {/if}
    {#if fieldBlock.required && hasError && fieldBlock.type !== 'splitField'}
        <span class="text-danger fw-bold">{mtappVars.translate['This field is required']}</span>
    {/if}
</div>
